import React, { ElementRef, useEffect, useRef } from "react";
import { EmbedRatio } from "./EmbedRatio";
import { Script } from "gatsby";
import { useConst } from "@chakra-ui/react";
import { useLocalStorage } from "react-use";
import { useUser } from "@utils";

export type VideoDataProps = {
  uploadDate: number;
  thumbnailUrl: string;
};

type WistiaConfig = {
  id: string;
  onReady: (video: WistiaPlayer.Player) => void;
};

declare global {
  interface Window {
    _wq: (WistiaConfig | { revoke: WistiaConfig })[];
  }
}

type CaptionsConfig = {
  onByDefault?: boolean;
  language?: string;
};

type WistiaEmbedProps = {
  id: string;
  handleVideoData: (data: VideoDataProps) => void;
  pip?: boolean;
};

export function WistiaEmbed({
  id,
  handleVideoData,
  pip = true,
}: WistiaEmbedProps) {
  const [captions, setCaptions] =
    useLocalStorage<CaptionsConfig>("captions-v1");

  const embedRef = useRef<ElementRef<typeof EmbedRatio>>(null);
  const videoRef = useRef<WistiaPlayer.Player | null>(null);

  useEffect(
    () => {
      const wistiaConfig: WistiaConfig = {
        id,
        onReady: (video) => {
          // generate thumbnail from screencap: https://wistia.com/support/developers/extracting-thumbnails
          const videoUrl = video._mediaData.assets[0].url;
          const thumbnailUrl = `${videoUrl.replace(
            "bin",
            "jpg"
          )}?video_still_time=10`;
          handleVideoData({
            uploadDate: video._mediaData.createdAt,
            thumbnailUrl,
          });
          videoRef.current = video;
          video.bind("end", () => embedRef.current?.closePip()); // close pip on video end
          video.bind("captionschange", ({ visible, language }) => {
            // save captions preferences
            setCaptions({
              onByDefault: visible,
              language,
            });
          });
        },
      };

      // configure the wistia player
      // see https://wistia.com/support/developers/player-api
      window._wq = window._wq || [];
      window._wq.push(wistiaConfig);

      return () => {
        window._wq.push({ revoke: wistiaConfig });
      };
    },
    // we want to make sure this effect runs only once, so we pass an empty
    // dependency array even though eslint wants us to add `id` and
    // `setCaptions`
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const { user } = useUser();

  const query = useConst(() => {
    const captionConfig =
      captions &&
      Object.entries(captions).reduce(
        (acc, [key, value]) => ({
          ...acc,
          [`plugin[captions-v1][${key}]`]: value,
        }),
        {}
      );

    const searchParams = new URLSearchParams({
      endVideoBehavior: "reset",
      fitStrategy: "none",
      ...captionConfig,
    });

    if (user) {
      searchParams.append("email", user.id);
    }

    return searchParams.toString();
  });

  return (
    <div>
      <Script src="https://fast.wistia.net/assets/external/E-v1.js" />
      <EmbedRatio
        pip={pip}
        ref={embedRef}
        title={id}
        className="wistia_embed"
        name="wistia_embed"
        src={`//fast.wistia.net/embed/iframe/${id}?seo=true&${query}`}
        onPipClose={() => {
          videoRef.current?.pause();
        }}
      />
    </div>
  );
}
